import React, { useState, useRef, useCallback, useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import http from "../../Utils/axiosConfig";
import { apiMessage } from "../../Utils/apiMessage";
import ToastService from "../../Utils/ToastService";
import { TYPE_LABEL } from "../../Utils/commonFuntion";
import "../DairyRegister.css";
import "./Main/Dashboard.css";

const MEMBER_PANEL_URL =
  process.env.REACT_APP_MEMBER_PANEL_URL || "https://dairyuser.eprosolutions.in/";

// wa.me wants a country-coded number with no +, spaces or dashes. Members are
// stored as 10 digits, but be tolerant of a stored 91-prefixed or +91 number.
const waNumber = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 10) return "91" + digits;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return null;
};

const loginMessage = ({ name, dairy_id, username, password }) =>
  [
    `नमस्कार ${name},`,
    `क्रमांक ${dairy_id} — ${localStorage.getItem("dairy_name") || "आमची दूध संस्था"}`,
    "",
    "तुमची दूध नोंद, बिल, ऍडव्हान्स व पशुखाद्य तुम्ही येथे पाहू शकता:",
    MEMBER_PANEL_URL,
    "",
    `युझरनेम: ${username}`,
    `पासवर्ड: ${password}`,
    "",
    "हा मेसेज कोणालाही पाठवू नका.",
  ].join("\n");

// Shared by the add form and the edit dialog so the two cannot drift apart. The
// dairy number is only checked when adding — it is not editable afterwards, since
// the paper register, printed bills and bulk-upload sheets all key on it.
const validateFields = (d, { withNumber = false, withUsername = false } = {}) => {
  const e = {};

  if (!d.englishName.trim()) e.englishName = "इंग्रजी नाव आवश्यक आहे";
  else if (!/^[A-Za-z ]+$/.test(d.englishName)) e.englishName = "फक्त इंग्रजी अक्षरे टाका";

  if (!d.marathiName.trim()) e.marathiName = "मराठी नाव आवश्यक आहे";

  if (withNumber) {
    if (!d.number.trim()) e.number = "क्रमांक आवश्यक आहे";
    else if (isNaN(d.number)) e.number = "क्रमांक फक्त अंक असावा";
  }

  if (!d.mobile.trim()) e.mobile = "मोबाईल नंबर आवश्यक आहे";
  else if (!/^[0-9]{10}$/.test(d.mobile)) e.mobile = "मोबाईल नंबर 10 अंकांचा असावा";

  if (!d.type) e.type = "प्रकार निवडा";

  // Only when the field is on screen — the add form derives the username from the
  // English name and क्रमांक, so there is nothing to validate there.
  if (withUsername) {
    const name = (d.username || "").trim().toLowerCase();
    if (!name) e.username = "युझरनेम आवश्यक आहे";
    else if (!/^[a-z0-9._]{4,50}$/.test(name)) {
      e.username = "4-50 अक्षरे, फक्त a-z, 0-9, . आणि _";
    }
  }

  return e;
};

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const AddMember = () => {
  const [tab, setTab] = useState(0);

  const [formData, setFormData] = useState({
    englishName: "",
    marathiName: "",
    number: "",
    mobile: "",
    type: "buffalo",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const marathiTouchedRef = useRef(false);

  // The member open in the edit dialog, held in the add form's field shape
  const [editing, setEditing] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const editMrTouchedRef = useRef(false);
  const editOrigEnglishRef = useRef("");

  const [members, setMembers] = useState([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const dairy_id = localStorage.getItem("temp_key");
      const res = await http.get("member/get_all_member", { params: { id: dairy_id } });
      setMembers(res?.code === "DATA_FOUND" ? res.data : []);
    } catch (err) {
      console.error("Error fetching members:", err);
      ToastService.error("सभासद यादी मिळवण्यात अयशस्वी");
    } finally {
      setMembersLoading(false);
      setMembersLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (tab === 1 && !membersLoaded) {
      fetchMembers();
    }
  }, [tab, membersLoaded, fetchMembers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "marathiName") {
      marathiTouchedRef.current = true;
    }
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" }); // clear error when typing
  };

  const fetchMarathiName = async (text) => {
    try {
      const res = await http.get("member/transliterate", { params: { text } });
      return res?.code === "DATA_FOUND" ? res.data.marathiName : null;
    } catch (err) {
      console.error("Transliteration error:", err);
      return null;
    }
  };

  // Auto-fill मराठी नाव via transliteration once the user leaves इंग्रजी नाव,
  // unless they've already edited मराठी नाव themselves.
  const handleEnglishNameBlur = async () => {
    const text = formData.englishName.trim();
    if (marathiTouchedRef.current || !text) return;

    const marathiName = await fetchMarathiName(text);
    if (marathiName && !marathiTouchedRef.current) {
      setFormData((prev) => ({ ...prev, marathiName }));
    }
  };

  const openEdit = (m) => {
    setEditing({
      id: m.id,
      dairy_id: m.dairy_id,
      englishName: m.en_name || "",
      marathiName: m.mr_name || "",
      mobile: m.phone || "",
      type: m.type || "buffalo",
      username: m.username || "",
    });
    setEditErrors({});
    editMrTouchedRef.current = false;
    editOrigEnglishRef.current = m.en_name || "";
  };

  // Opens WhatsApp with the member's login details prefilled. The credentials are
  // fetched per click rather than listed with every member, so a plaintext password
  // only ever crosses the wire for the one member being messaged.
  const sendLoginDetails = async (m) => {
    const number = waNumber(m.phone);
    if (!number) {
      ToastService.error("या सभासदाचा मोबाईल नंबर नाही — प्रथम संपादन करून टाका");
      return;
    }

    setSendingTo(m.id);
    try {
      const res = await http.post("member/get-credentials", { id: m.id });
      if (res?.code !== "DATA_FOUND") {
        ToastService.error(res?.data?.reason || "लॉगिन तपशील मिळाले नाहीत ❌");
        return;
      }
      const text = encodeURIComponent(loginMessage(res.data));
      // noopener: the WhatsApp tab must not get a handle on this window.
      window.open(`https://wa.me/${number}?text=${text}`, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error fetching member credentials:", err);
      ToastService.error(apiMessage());
    } finally {
      setSendingTo(null);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "marathiName") {
      editMrTouchedRef.current = true;
    }
    setEditing((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Same courtesy as the add form, but only when the English name actually changed —
  // otherwise merely tabbing through the dialog would overwrite the मराठी नाव the
  // dairy already has on record.
  const handleEditEnglishNameBlur = async () => {
    const text = editing?.englishName.trim();
    if (!text || editMrTouchedRef.current || text === editOrigEnglishRef.current.trim()) return;

    const marathiName = await fetchMarathiName(text);
    if (!marathiName || editMrTouchedRef.current) return;
    // The dialog may have been closed while the request was in flight.
    setEditing((prev) => (prev ? { ...prev, marathiName } : prev));
    setEditErrors((prev) => ({ ...prev, marathiName: "" }));
  };

  const saveEdit = async () => {
    const validationErrors = validateFields(editing, { withUsername: true });
    if (Object.keys(validationErrors).length > 0) {
      setEditErrors(validationErrors);
      return;
    }

    try {
      setEditSaving(true);
      const res = await http.post("member/update-member", editing);

      if (res?.code === "MEMBER_UPDATED") {
        ToastService.success("सभासद अद्ययावत झाला ✅");
        // The server returns the saved row, so patch it in instead of refetching.
        setMembers((prev) => prev.map((m) => (m.id === res.data.id ? { ...m, ...res.data } : m)));
        setEditing(null);
      } else if (res?.code === "USERNAME_ALREADY_EXISTS") {
        setEditErrors((prev) => ({ ...prev, username: "हा युझरनेम आधीच वापरात आहे" }));
      } else {
        ToastService.error(res?.data?.reason || "बदल जतन झाला नाही ❌");
      }
    } catch (err) {
      console.error("Error updating member:", err);
      ToastService.error(apiMessage());
    } finally {
      setEditSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateFields(formData, { withNumber: true });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      const response = await http.post("member/add-member", formData);

      if (response.code === "MEMBER_ADDED") {
        ToastService.success("नोंद पूर्ण झाली ✅");
        setFormData({
          englishName: "",
          marathiName: "",
          number: "",
          mobile: "",
          type: "buffalo",
        });
        setErrors({});
        marathiTouchedRef.current = false;
        setMembersLoaded(false);
      } else if (response.code === "DAIRY_ID_ALREADY_EXISTS") {
        ToastService.error("हा क्रमांक आधीच वापरात आहे ❌");
      } else {
        ToastService.error(apiMessage(response, "त्रुटी आली ❌"));
      }

    } catch (error) {
      console.error("❌ Error submitting form:", error);
      ToastService.error(apiMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dre-container">
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        centered
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: "#ff9800" } }}
        sx={{
          "& .MuiTab-root": { color: "#bbb", fontWeight: "bold", fontSize: "16px" },
          "& .Mui-selected": { color: "#ff9800" },
        }}
      >
        <Tab label="सभासद जोडा" />
        <Tab label="सभासद यादी" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <form className="dre-form" onSubmit={handleSubmit}>
          <div className="dre-field dre-inline">
            <label className="dre-field-label">इंग्रजी नाव</label>
            <input
              type="text"
              name="englishName"
              className="dre-input"
              value={formData.englishName}
              onChange={handleChange}
              onBlur={handleEnglishNameBlur}
            />
          </div>
          {errors.englishName && <p className="dre-error">{errors.englishName}</p>}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">मराठी नाव</label>
            <input
              type="text"
              name="marathiName"
              className="dre-input"
              value={formData.marathiName}
              onChange={handleChange}
            />
          </div>
          {errors.marathiName && <p className="dre-error">{errors.marathiName}</p>}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">क्रमांक</label>
            <input
              type="text"
              name="number"
              className="dre-input"
              value={formData.number}
              onChange={handleChange}
            />
          </div>
          {errors.number && <p className="dre-error">{errors.number}</p>}

          <div className="dre-field dre-inline">
            <label className="dre-field-label">मोबाईल नंबर</label>
            <input
              type="tel"
              name="mobile"
              className="dre-input"
              value={formData.mobile}
              onChange={handleChange}
            />
          </div>
          {errors.mobile && <p className="dre-error">{errors.mobile}</p>}

          <div className="dre-field dre-radio-row">
            <label className="dre-field-label">प्रकार</label>
            <div className="dre-radios">
              <label className="dre-radio-label">
                <input
                  type="radio"
                  name="type"
                  value="buffalo"
                  checked={formData.type === "buffalo"}
                  onChange={handleChange}
                />
                म्हैस
              </label>
              <label className="dre-radio-label">
                <input
                  type="radio"
                  name="type"
                  value="cow"
                  checked={formData.type === "cow"}
                  onChange={handleChange}
                />
                गाय
              </label>
            </div>
          </div>
          {errors.type && <p className="dre-error">{errors.type}</p>}

          <div className="dre-submit-row">
            <button type="submit" className="dre-submit-btn" disabled={loading}>
              {loading ? "जतन होत आहे..." : "OKAY"}
            </button>
          </div>
        </form>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <div className="dre-detail">
          {membersLoading ? (
            <p className="dal-status">Loading...</p>
          ) : members.length === 0 ? (
            <p className="dal-status">कोणतेही सभासद आढळले नाहीत.</p>
          ) : (
            <div className="dre-table-wrap">
              <table className="dre-table">
                <thead>
                  <tr>
                    <th>क्रमांक</th>
                    <th>नाव</th>
                    <th>युझरनेम</th>
                    <th>मोबाईल</th>
                    <th>प्रकार</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td>{m.dairy_id}</td>
                      <td>{m.mr_name || m.en_name}</td>
                      <td className="dre-username">{m.username || "-"}</td>
                      <td>{m.phone || "-"}</td>
                      <td>{TYPE_LABEL[m.type] || m.type}</td>
                      <td className="dre-row-actions">
                        <button className="dre-row-btn" onClick={() => openEdit(m)}>संपादन</button>
                        <button className="dre-row-btn dre-row-btn--wa" title="लॉगिन तपशील WhatsApp वर पाठवा"
                          onClick={() => sendLoginDetails(m)} disabled={sendingTo === m.id}>
                          {sendingTo === m.id ? "..." : "🟢 लॉगिन"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </TabPanel>

      {editing && (
        <div className="bd-confirm-overlay" onClick={() => !editSaving && setEditing(null)}>
          <div className="bd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>क्रमांक {editing.dairy_id} — सभासद संपादन</h3>

            <div className="dre-modal-form">
              <div className="dre-modal-field">
                <div className="dre-field dre-inline">
                  <label className="dre-field-label">इंग्रजी नाव</label>
                  <input type="text" name="englishName" className="dre-input"
                    value={editing.englishName} onChange={handleEditChange}
                    onBlur={handleEditEnglishNameBlur} />
                </div>
                {editErrors.englishName && <p className="dre-error">{editErrors.englishName}</p>}
              </div>

              <div className="dre-modal-field">
                <div className="dre-field dre-inline">
                  <label className="dre-field-label">मराठी नाव</label>
                  <input type="text" name="marathiName" className="dre-input"
                    value={editing.marathiName} onChange={handleEditChange} />
                </div>
                {editErrors.marathiName && <p className="dre-error">{editErrors.marathiName}</p>}
              </div>

              <div className="dre-modal-field">
                <div className="dre-field dre-inline">
                  <label className="dre-field-label">मोबाईल</label>
                  <input type="tel" name="mobile" className="dre-input"
                    value={editing.mobile} onChange={handleEditChange} />
                </div>
                {editErrors.mobile && <p className="dre-error">{editErrors.mobile}</p>}
              </div>

              <div className="dre-modal-field">
                <div className="dre-field dre-inline">
                  <label className="dre-field-label">युझरनेम</label>
                  <input type="text" name="username" className="dre-input dre-username"
                    value={editing.username} onChange={handleEditChange}
                    autoCapitalize="none" spellCheck="false" maxLength={50} />
                </div>
                {editErrors.username && <p className="dre-error">{editErrors.username}</p>}
              </div>

              <div className="dre-field dre-radio-row">
                <label className="dre-field-label">प्रकार</label>
                <div className="dre-radios">
                  <label className="dre-radio-label">
                    <input type="radio" name="type" value="buffalo"
                      checked={editing.type === "buffalo"} onChange={handleEditChange} />
                    म्हैस
                  </label>
                  <label className="dre-radio-label">
                    <input type="radio" name="type" value="cow"
                      checked={editing.type === "cow"} onChange={handleEditChange} />
                    गाय
                  </label>
                </div>
              </div>
            </div>

            {/* The password is left alone — it holds the member's own secret, so a new
                mobile number does not reset it. A changed युझरनेम does change how they
                log in, so send them the new details afterwards. */}
            <div className="bd-confirm-actions">
              <button className="submitbtn active" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? "जतन होत आहे..." : "जतन करा"}
              </button>
              <button className="submitbtn secondary" onClick={() => setEditing(null)} disabled={editSaving}>
                रद्द करा
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddMember;
